// btw-front/src/components/ui/FileAttachment.jsx
import React, { useRef } from "react";

const KB = 1024;
const MB = KB * 1024;

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  if (bytes >= KB) return `${(bytes / KB).toFixed(0)} KB`;
  return `${bytes} B`;
}

/**
 * 단일 파일 첨부 컴포넌트 (목업: 실제 업로드 X, File 객체만 보유).
 *
 * props
 *  - label, hint, required
 *  - value: File | null
 *  - onChange(file: File | null): void
 *  - accept: string ('.pdf,.doc,.docx' 등)
 *  - maxSizeMb: number — 초과 시 onError 호출 후 무시
 *  - onError(messageKey): void
 *  - buttonLabel, attachedLabel, removeLabel
 */
export function FileAttachment({
  label,
  hint,
  required = false,
  value,
  onChange,
  accept = "",
  maxSizeMb = 10,
  onError,
  buttonLabel = "",
  attachedLabel = "",
  removeLabel = "",
  error,
  className = "",
}) {
  const inputRef = useRef(null);

  function openPicker() {
    inputRef.current?.click();
  }

  function handlePick(e) {
    const f = e.target.files?.[0];
    e.target.value = ""; // 같은 파일 다시 선택 가능하게
    if (!f) return;
    if (maxSizeMb && f.size > maxSizeMb * MB) {
      if (onError) onError("size");
      return;
    }
    onChange?.(f);
  }

  function handleRemove() {
    onChange?.(null);
  }

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && (
        <label className="text-xs font-medium text-slate-700">
          {label}
          {required && <span className="ml-0.5 text-rose-500">*</span>}
        </label>
      )}

      {value ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
          <span aria-hidden="true" className="text-base">
            📎
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-slate-800" title={value.name}>
              {value.name}
            </div>
            <div className="text-[11px] text-slate-500">
              <span className="mr-1.5 inline-block rounded-full bg-emerald-50 px-1.5 text-[10px] text-emerald-700">
                {attachedLabel}
              </span>
              {formatSize(value.size)}
            </div>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="text-xs font-medium text-rose-600 underline-offset-2 hover:underline"
          >
            {removeLabel}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 text-sm text-slate-600 transition hover:border-accent hover:text-accent"
        >
          <span aria-hidden="true">⬆️</span>
          {buttonLabel}
        </button>
      )}

      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handlePick} />

      {error ? (
        <p className="text-xs text-rose-600">{error}</p>
      ) : hint ? (
        <p className="text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}
